import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { RetentionTabContent } from '@/components/hook-track/metrics/tabs/RetentionTabContent';
import { MetricsTabContent } from '@/components/hook-track/metrics/tabs/MetricsTabContent';
import { VideoPerformance } from '@/components/hook-track/metrics/HookTrackPerformanceTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { PeriodPicker } from '@/components/ui/period-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { buildPresetRange } from '@/utils/period-presets';
import { useHookTrackStore } from '@/store/hook-track/hookTrackStore';
import { useTrackingData } from '@/components/tracking/useTrackingData';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart } from 'lucide-react';

export default function HookTrackMetrics() {
  useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { currentTest, metrics, isLoading, fetchTestById, fetchTestMetrics } = useHookTrackStore();

  // Date range state
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    buildPresetRange('last7Days', new Date())
  );

  // Métricas de rastreamento (conversões/receita) por vídeo, no mesmo período.
  // Reusa o endpoint do Tracking; casa por id_video da intro.
  const { data: trackingData } = useTrackingData(dateRange);
  const trackingByVideo = React.useMemo(() => {
    const map: Record<string, { conversions: number; revenue: number }> = {};
    (trackingData?.integrations || []).forEach((integration) => {
      if (!integration.id_video) return;
      const key = String(integration.id_video);
      if (!map[key]) map[key] = { conversions: 0, revenue: 0 };
      map[key].conversions += Number(integration.total_conversions) || 0;
      map[key].revenue += Number(integration.total_revenue) || 0;
    });
    return map;
  }, [trackingData]);

  // Buscar dados do teste e métricas
  useEffect(() => {
    if (id) {
      fetchTestById(id);
    }
  }, [id, fetchTestById]);

  useEffect(() => {
    if (id && dateRange?.from && dateRange?.to) {
      const inicio = format(dateRange.from, 'yyyy-MM-dd');
      const fim = format(dateRange.to, 'yyyy-MM-dd');
      fetchTestMetrics(id, inicio, fim);
    }
  }, [id, dateRange, fetchTestMetrics]);

  // Test data from store
  const testName = currentTest?.name || currentTest?.leadTestTitle || currentTest?.title || "Teste de Hook";
  const testStatus: 'active' | 'paused' | 'finished' = currentTest?.is_active
    ? 'active'
    : (currentTest?.status as 'active' | 'paused' | 'finished') || 'active';
  const numberOfIntros = metrics.filter((item: any) => {
    const leadTestData = item.leadTest || {};
    return leadTestData.id_video !== currentTest?.originalVideoId;
  }).length;

  // Converter métricas para formato do componente
  const videoPerformanceData: VideoPerformance[] = metrics.length > 0
    ? metrics.map((item: any, index) => {
        // A API retorna { leadTest: {...}, metric: { totais: {...}, retencao: {...}, ... } }
        const leadTestData = item.leadTest || {};
        const metricData = item.metric || {};
        const totais = metricData.totais || {};
        const videoId = leadTestData.id_video;

        const isMainVideo = videoId === currentTest?.originalVideoId;
        const introVideos = metrics.filter((m: any) => {
          const mLeadTest = m.leadTest || {};
          return mLeadTest.id_video !== currentTest?.originalVideoId;
        });
        const introIndex = introVideos.findIndex((m: any) => {
          const mLeadTest = m.leadTest || {};
          return mLeadTest.id_video === videoId;
        });

        const trackingMetric = trackingByVideo[String(videoId)];
        const conversions = trackingMetric?.conversions ?? 0;
        const revenue = trackingMetric?.revenue ?? 0;

        return {
          id: videoId,
          name: leadTestData.nomeVideo || 'Sem nome',
          thumbnail: leadTestData.frame || '',
          isMain: isMainVideo,
          introNumber: !isMainVideo && introIndex >= 0 ? introIndex + 1 : undefined,
          views: parseInt(totais.visualizacoes || 0),
          uniqueViews: parseInt(totais.visualizacoesUnicas || 0),
          plays: parseInt(totais.plays || 0),
          uniquePlays: parseInt(totais.playsUnicos || 0),
          playRate: parseFloat(Number(totais.taxareproducao || 0).toFixed(2)),
          engagement: parseFloat(Number(totais.engajamento || 0).toFixed(2)),
          // Raiz do retorno de MetricService.execute (não em `totais`): um item
          // por pitch do vídeo, com a retenção de cada um.
          pitches: Array.isArray(metricData.pitches) ? metricData.pitches : [],
          pitchVideoId: metricData.pitchVideoId ?? null,
          videoDuration: metricData.videoDuration ?? null,
          conversions,
          revenue,
          averageTicket: conversions > 0 ? revenue / conversions : 0,
        };
      })
    : [];

  // Este teste tem alguma integração de rastreamento? (controla a exibição das colunas de conversão)
  const hasTrackingData = videoPerformanceData.some(v => trackingByVideo[String(v.id)] !== undefined);

  // Videos for retention chart
  const retentionVideos = videoPerformanceData.map(v => ({
    id: v.id,
    name: v.name,
    isMain: v.isMain
  }));

  // Find winner video name (baseado na melhor performance - engagement)
  const introVideos = videoPerformanceData.filter(v => !v.isMain);
  const winnerVideo = introVideos.length > 0
    ? introVideos.reduce((prev, current) =>
        (current.engagement > prev.engagement) ? current : prev
      )
    : undefined;
  const winnerVideoName = winnerVideo
    ? `Intro #${winnerVideo.introNumber} – ${winnerVideo.name}`
    : undefined;

  // Calcular duração máxima dos vídeos
  const maxVideoDuration = metrics.length > 0
    ? Math.max(...metrics.map((item: any) => {
        const metricData = item.metric || {};
        const totais = metricData.totais || {};
        return totais.duracaoVideo || 300;
      }))
    : 300; // Default: 5 minutos

  // Processar dados reais de retenção da API
  const retentionData = React.useMemo(() => {
    if (metrics.length === 0) return [];

    // Coletar todos os tempos únicos de todos os vídeos
    const allTimesSet = new Set<string>();
    const videoRetentionMap = new Map<string, any>();

    metrics.forEach((item: any) => {
      const leadTestData = item.leadTest || {};
      const metricData = item.metric || {};
      const retencao = metricData.retencao || {};
      const videoId = leadTestData.id_video;

      videoRetentionMap.set(videoId, retencao);
      Object.keys(retencao).forEach(time => allTimesSet.add(time));
    });

    // Converter tempos "MM:SS" para segundos e ordenar
    const sortedTimes = Array.from(allTimesSet)
      .map(time => {
        const [min, sec] = time.split(':').map(Number);
        return { timeStr: time, seconds: min * 60 + sec };
      })
      .sort((a, b) => a.seconds - b.seconds);

    // Criar array de dados de retenção - SIMPLES: usa exatamente o que a API retorna
    const retentionArray: any[] = sortedTimes.map(({ timeStr, seconds }) => {
      const dataPoint: any = { time: seconds };

      metrics.forEach((item: any) => {
        const leadTestData = item.leadTest || {};
        const videoId = leadTestData.id_video;
        const retencao = videoRetentionMap.get(videoId) || {};

        // Se a API tem dados para este tempo, usa o valor
        // Se não tem, deixa undefined (linha não desenha naquele ponto)
        if (retencao[timeStr]?.percent !== undefined) {
          dataPoint[videoId] = retencao[timeStr].percent;
        }
      });

      return dataPoint;
    });

    return retentionArray;
  }, [metrics]);

  const statusLabels = {
    active: 'Ativo',
    paused: 'Pausado',
    finished: 'Finalizado'
  };

  // Premium badge styles with 10% opacity + 1px border
  const statusStyles = {
    active: {
      background: 'rgba(45, 108, 246, 0.1)',
      border: '1px solid #2D6CF6',
      color: '#2D6CF6',
    },
    paused: {
      background: 'rgba(230, 81, 0, 0.1)',
      border: '1px solid #E65100',
      color: '#E65100',
    },
    finished: {
      background: 'rgba(107, 114, 128, 0.1)',
      border: '1px solid #6B7280',
      color: '#6B7280',
    }
  };

  // Loading state
  if (isLoading && !currentTest) {
    return (
      <Layout fixedSidebar>
        <div className="flex-1 flex flex-col bg-background overflow-hidden">
          <div className="bg-background border-b border-[#E5E7EB] px-4 lg:px-[60px] py-6">
            <div className="max-w-[1320px] mx-auto">
              <Skeleton className="h-8 w-96 mb-4" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-8">
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout fixedSidebar>
      <div className="flex-1 flex flex-col bg-background overflow-hidden">
        {/* Premium Header */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
            {/* Título */}
            <h1 className="text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {translateInterfaceText("Desempenho do Seu Teste de Introduções")}</h1>
          </div>
          <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />

          {/* Breadcrumb */}
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 pb-2">
            <div className="flex items-center gap-2">
              {/* Link clicável - apenas este elemento */}
              <button
                onClick={() => navigate('/hook-track/list')}
                className="text-[14px] font-medium hover:underline transition-all cursor-pointer"
                style={{ color: '#404040' }}
              >
                {translateInterfaceText("Teste:")}{" "}{testName}
              </button>

              {/* Divisor cinza claro */}
              <span className="text-[14px]" style={{ color: '#9E9E9E' }}>{">"}</span>

              {/* Número de introduções - não clicável */}
              <span className="text-[14px]" style={{ color: '#9E9E9E' }}>
                {numberOfIntros} {" "}{translateInterfaceText("introduções")}</span>

              {/* Divisor cinza claro */}
              <span className="text-[14px]" style={{ color: '#9E9E9E' }}>{">"}</span>

              {/* Badge status */}
              <Badge
                className="text-xs font-bold px-2.5 py-1 rounded-md"
                style={statusStyles.active}
              >
                {statusLabels.active}
              </Badge>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#C5C5C5 transparent' }}>
          <div className="px-4 lg:px-[60px] py-8 max-w-[1320px] mx-auto">
            {videoPerformanceData.length === 0 && !isLoading ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <BarChart className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-[15px] text-gray-600 font-medium">
                  {translateInterfaceText("Nenhum dado disponível ainda")}</p>
                <p className="text-[13px] text-gray-500 mt-1">
                  {translateInterfaceText("As métricas aparecerão quando houver visualizações nos vídeos do teste")}</p>
              </div>
            ) : (
              <Tabs defaultValue="retention" className="w-full">
              {/* Gap entre título e tabs: 28px */}
              <div className="flex items-center justify-between mb-7">
                <TabsList>
                  <TabsTrigger value="retention">
                    {translateInterfaceText("Retenção")}</TabsTrigger>
                  <TabsTrigger value="metrics">
                    {translateInterfaceText("Métricas")}</TabsTrigger>
                </TabsList>

                <PeriodPicker
                  value={dateRange}
                  onChange={setDateRange}
                  align="end"
                  className="md:min-w-[240px]"
                />
              </div>

              {/* Gap entre tabs e gráfico: 30px */}
              <TabsContent value="retention" className="mt-7 animate-fade-in">
                <RetentionTabContent
                  videos={retentionVideos}
                  retentionData={retentionData}
                  winnerVideoId={winnerVideo?.id}
                  winnerVideoName={winnerVideoName}
                  hasData={videoPerformanceData.length > 0}
                  maxVideoDuration={maxVideoDuration}
                />
              </TabsContent>

              <TabsContent value="metrics" className="mt-7 animate-fade-in">
                <MetricsTabContent
                  videos={videoPerformanceData}
                  showTotals={true}
                  hasTracking={hasTrackingData}
                  onPitchSaved={() => {
                    // Recarrega as métricas: a retenção no pitch é recalculada
                    // no backend a partir do novo tempo salvo. Devolve a promessa
                    // para o modal só fechar com a lista nova (o próximo save
                    // envia a lista inteira).
                    if (id && dateRange?.from && dateRange?.to) {
                      return fetchTestMetrics(
                        id,
                        format(dateRange.from, 'yyyy-MM-dd'),
                        format(dateRange.to, 'yyyy-MM-dd'),
                      );
                    }
                  }}
                />
              </TabsContent>
            </Tabs>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
